import React, { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';
import AppContentContainer from '../../../../components/AppContentContainer/AppContentContainer';
import StudioEditor from '../../Tools/hotTopicStudio/Editor/component';
import { STUDIO_TRACKING_SOURCE } from '../../Tools/hotTopicStudio/utils/studioTracking';
import { newsToStudioPack, type NewsStudioItem } from './newsToStudioPack';

const host = process.env.HEADLESS_CMS_URL || '';

const PAGE_STYLE: React.CSSProperties = {
  maxWidth: 'none',
  paddingTop: 45,
  paddingRight: 0,
  paddingBottom: 0,
  paddingLeft: 0
};

const findNewsItem = (newsItems: any[], newsId: string) =>
  (newsItems || []).find((item: any) => String(item.news_id) === newsId || String(item.id) === newsId);

const hasStudioPages = (item?: NewsStudioItem | null): item is NewsStudioItem =>
  !!item && Array.isArray(item.studioPackPages) && item.studioPackPages.length > 0;

interface NewsfeedStudioEditorProps {
  intl: any;
  history: any;
  location: { state?: { studioItem?: NewsStudioItem } };
  match: { params: { id: string; newsId: string } };
  userDetails: any;
  teamCampaignCodes: any[];
  teams: any[];
  renderHeaderButton: (show: boolean) => void;
  updateUserDetails: (data: any) => void;
}

const NewsfeedStudioEditor: React.FC<NewsfeedStudioEditorProps> = props => {
  const { intl, history, location, match, ...editorProps } = props;
  const newsfeedId = match.params.id;
  const newsId = match.params.newsId;
  const notFoundBackPath = `/selected_newsfeed/${newsfeedId}`;
  const rawStateItem = location && location.state ? location.state.studioItem : undefined;
  const stateStudioItem = hasStudioPages(rawStateItem) ? rawStateItem : undefined;
  const [studioItem, setStudioItem] = useState<NewsStudioItem | undefined>(stateStudioItem);
  const [loading, setLoading] = useState(!stateStudioItem);

  useEffect(() => {
    if (stateStudioItem) {
      setStudioItem(stateStudioItem);
      setLoading(false);
      return undefined;
    }

    let cancelled = false;
    setLoading(true);

    fetch(`${host}/api/selected-newsfeeds/${newsfeedId}?populate[news][populate]=*`)
      .then(res => res.json())
      .then(data => {
        if (cancelled) {
          return;
        }
        const newsItems = (data && data.data && data.data.attributes && data.data.attributes.news) || [];
        const news = findNewsItem(newsItems, newsId);
        setStudioItem(newsToStudioPack(news) || undefined);
      })
      .catch(err => {
        console.error('Error fetching newsfeed studio pack:', err);
        if (!cancelled) {
          setStudioItem(undefined);
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [newsId, newsfeedId, stateStudioItem]);

  if (loading) {
    return (
      <AppContentContainer className="hot-topic-studio-editor-page" style={PAGE_STYLE}>
        <div className="studio-editor-state embedded">
          <CircularProgress color="primary" />
        </div>
      </AppContentContainer>
    );
  }

  return (
    <StudioEditor
      {...editorProps}
      intl={intl}
      history={history}
      match={{ params: { templateId: (studioItem && String(studioItem.id)) || newsId, id: newsfeedId, newsId } }}
      studioItem={studioItem}
      notFoundBackPath={notFoundBackPath}
      trackingSource={STUDIO_TRACKING_SOURCE.CUSTOM_NEWSFEED}
    />
  );
};

export default NewsfeedStudioEditor;
